import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import {
    TLoadable,
    isLoaded,
    createLoaded,
    isLoading,
    createUnloaded,
    createLoading,
    isUnloaded,
} from "../../../../sg-core";
import { selectHavainnotState } from "../selectors";
import IFormType from "../../interfaces/IFormType";
import { createRequestFetchFormDefinition } from "../actions/formActions";
import IFormDefinition from "../../interfaces/IFormDefinition";

// SELECTORS
export const selectFormContextState = createSelector(selectHavainnotState, (workPermit) => workPermit.form);

export const selectFormTypesByIdState = createSelector(selectFormContextState, (state) => state.formTypes);

export const selectFormTypesByGuidState = createSelector(
    selectFormContextState,
    (state) => state.formTypesByGuid,
);

const selectFormTypesIdList = createSelector(selectFormContextState, (state) => state.formTypeList);

const selectFormTypeList = createSelector(
    selectFormTypesIdList,
    selectFormTypesByIdState,
    (formTypeList, formTypes): TLoadable<IFormType[]> => {
        if (isLoaded(formTypeList)) {
            return createLoaded(formTypeList.data.map((id) => formTypes[id]));
        }
        return formTypeList as TLoadable<IFormType[]>;
    },
);

export const createSelectFormTypeById = (formTypeId: number | undefined) =>
    createSelector(selectFormTypesByIdState, (formTypes): IFormType | undefined => {
        return formTypeId ? formTypes[formTypeId] : undefined;
    });

export const createSelectFormTypeByGuid = (formTypeId: string | undefined) =>
    createSelector(selectFormTypesByGuidState, (formTypes): IFormType | undefined => {
        return formTypeId ? formTypes[formTypeId] : undefined;
    });

export const selectFromDefinitionByIdState = createSelector(
    selectFormContextState,
    (state) => state.definitions,
);

const selectCurrentFormDefiniton = createSelector(selectFormContextState, (state) => state.currentDefinition);

export const createSelectFormDefinitionById = (formId: number) =>
    createSelector(selectFromDefinitionByIdState, (definitions): IFormDefinition | undefined => {
        return definitions ? definitions[formId] : undefined;
    });

// HOOKS
export const useFormTypeList = (): TLoadable<IFormType[]> => useSelector(selectFormTypeList) as any;
export const useFormTypeById = (formTypeId: number | undefined): IFormType | undefined => {
    const isGuid = (formTypeId + "").length > 10;
    return useSelector(
        isGuid ? createSelectFormTypeByGuid(formTypeId + "") : createSelectFormTypeById(formTypeId),
    );
};
export const useFormTypeByGuid = (formTypeId: string | undefined): IFormType | undefined =>
    useSelector(createSelectFormTypeByGuid(formTypeId));

export const useFormTypeListByCategoryId = (categoryId: number): TLoadable<IFormType[]> => {
    const typeList = useFormTypeList();

    if (categoryId && isLoaded(typeList)) {
        return createLoaded(typeList.data.filter(({ category }) => category === categoryId));
    } else if (isLoaded(typeList)) {
        return createLoaded([]);
    }

    return typeList;
};

export const useFormDefinitionById = (formTypeId?: number | undefined): TLoadable<IFormDefinition> => {
    const dispatch = useDispatch();

    const selectedId = useSelector(selectCurrentFormDefiniton);
    const definitions = useSelector(selectFromDefinitionByIdState);

    if (
        !isLoading(selectedId) &&
        isLoaded(selectedId) &&
        formTypeId &&
        `${formTypeId}` !== `${selectedId.data}`
    ) {
        dispatch(createRequestFetchFormDefinition(formTypeId));
        return createUnloaded();
    }
    if (isUnloaded(selectedId) && formTypeId) {
        dispatch(createRequestFetchFormDefinition(formTypeId));
        return createLoading();
    }

    if (isLoaded(selectedId)) {
        return createLoaded(definitions[selectedId.data]);
    }
    return selectedId as TLoadable<IFormDefinition>;
};
